import React from "react";
import { format, hasValue, processImagePath } from "../../../../util/Functions";
import ProductAttributeVariationButton from "./ProductAttributeVariationButton";
import "./ProductAttribute.scss"
import parse from 'html-react-parser';

import Select from 'react-select';
import CatalogContext from "../../../../contexts/CatalogContext";
import { StringUtil } from "../../../../util/String.util";
import ProductInvariationDialog from "../../../../components/invariation/ProductInvariationDialog";

/**
 *
 * @param {InvariationOptions} invariationOptions
 * @param {ActionBus} actionBus
 */
export default function ProductAttribute({ attribute, data, currentProduct, variations, showLabel, changeItem, layout, children, invariationOptions, actionBus }) {
	const [catalog] = React.useContext(CatalogContext);

	const catalogProductOptions = catalog.options?.product_attribute;
	if (catalogProductOptions?.pricing?.hide_pricing && attribute.group?.label?.toLowerCase() === "pricing") {
		return <></>
	}

	const is_hidden = attribute.type === "hidden"
		|| attribute.reference === "id"
		|| (attribute.hide_blank && (data === "" || data === undefined || data === null))
		|| (attribute.hide_zero && (data === "0" || (!isNaN(data) && +data === 0)))
		|| (attribute.reference === 'pdf');

	if (is_hidden || (!hasValue(data) && !attribute.show_variations)) {
		return (<></>);
	}
	if (children) {
		data = '';
	}

	const innerChildren = [];

	// Show label if set to, or attribute prefers label and not set to false
	const show_label = attribute.show_label || (attribute.show_label === undefined && showLabel);
	show_label
		&& attribute.type !== 'json'
		&& innerChildren.push(<label key="label">{attribute.label}</label>);

	const fontSizes = { 'small': 'fs-smaller', 'big': 'fs-larger' };
	const fontWeights = { 'lighter': 'fw-lighter', 'bold': 'fw-bold' };
	const fontSize = fontSizes[attribute.font_size];
	const fontWeight = fontWeights[attribute.font_weight];

	const type = attribute.variations_selection || attribute.type;
	const classes = [
		type && 'type-' + type,
		attribute.reference.toLowerCase().replace(/ /g, '-'),
		variations?.length && 'has-variations',
		variations?.length > 1 && 'multi-variations',
		attribute?.show_image && 'has-image',
		fontSize,
		fontWeight,
	].filter(v => v).join(" ");

	const dataset = {
		"data-size": attribute.size,
	};

	const drawValue = (value, use_html = true) => {
		if (attribute.type === 'swatch') {
			return (<span key={'val-' + value} className='swatch' style={{ backgroundColor: value }}></span>);
		}

		let formatted = format(value, attribute);
		formatted = (typeof formatted === 'string' || formatted instanceof String)
			? parse(formatted)
			: formatted;

		if (attribute.dec_to_fract) {
			formatted = StringUtil.ToFraction(formatted);
		}
		return use_html ? (<span key='val' className='val'>{formatted}</span>) : formatted;
	}

	const addAdornments = (data, { icon, image } = {}, use_html = true) => {
		if (!hasValue(data)) {
			return drawValue(data);
		}

		if (!use_html) {
			const value = drawValue(data, false);
			return [attribute.prefix || '', value, attribute.suffix || ''];
		}
		return (
			<>
				{icon && (<img className='icon' src={icon} alt="" />)}
				{attribute?.show_image && image && (<img className='image' src={processImagePath(image, 'mini')} alt="" />)}
				{attribute.prefix && (<span key='pre' className='pre'>{attribute.prefix}</span>)}
				{drawValue(data)}
				{attribute.suffix && (<span key='suf' className='suf'>{attribute.suffix}</span>)}
			</>
		);
	}


	// Main value
	data && innerChildren.push((<div key="inner" className="inner">{addAdornments(data, {}, false)}</div>));

	let unavailable_suffix = attribute?.variation_unavailable_suffix;
	unavailable_suffix = (unavailable_suffix === undefined) ? 'Not Available' : unavailable_suffix;

	const allowInvariantVariationChange = invariationOptions?.allow_change;

	const productAttributeVariation_click = (e, variation) => {
		e?.stopPropagation();
		if (!allowInvariantVariationChange) {
			const distance = variation[2];
			if (distance > 1) {
				actionBus?.send(ProductInvariationDialog.Actions.INVARIATION_CHANGE, variation[1]);
				return;
			}
		}
		changeItem(variation[1]);
	}


	const selector_type = attribute.variations_selection;
	const getVariationButton = (variation, index) => (
		<ProductAttributeVariationButton
			key={index}
			onClick={e => productAttributeVariation_click(e, variation)}
			type={selector_type}
			attribute={attribute}
			color={variation[0]?.color}
			icon={variation[0]?.icon}
			image={variation[0].image}
			variation={variation}
			product={currentProduct}
			text={addAdornments(attribute.type === 'swatch' ? variation[0].color : variation[0].text)}
			active={variation[2] === 1}
			layout={layout}

			selected={currentProduct.id === +variation[1] || variation[0].text === String(data)}
			dataId={variation[1]}
			distance={variation[2]}
		/>
	);
	const getVariationSelectOption = (variation, index) => ({
		value: variation[1],
		key: index,
		label:
			(<span className="variation-option">
				{addAdornments(variation[0].text, variation[0])}
				{(variation[2] > 1 ? (<i>{unavailable_suffix}</i>) : '')}
			</span>)
	});
	const getVariationsSelector = () => {
		if (selector_type !== 'select') {
			return variations?.map(getVariationButton);
		} else {
			const options = variations?.map(getVariationSelectOption);
			const data_index = variations?.findIndex(v => v[0].text === String(data));
			const colorStyles = {
				option: (styles, { isFocused }) => {
					return {
						...styles,
						backgroundColor: isFocused ? "#999999" : null,
						color: isFocused ? "#ffffff" : "#333333",
					};
				}
			};
			const findVariation = value => variations?.find(v => v[1] === value);
			return (
				<>
					<Select
						onChange={e => productAttributeVariation_click(null, findVariation(e.value))}
						value={options[data_index]}
						isSearchable={false}
						className="variation-select"
						layout={layout}
						options={options}
						styles={colorStyles}
					/>
				</>
			);
		}
	};
	if (variations?.length > 1) {
		const variation_options = getVariationsSelector();
		innerChildren.push(<div key="variations" className="variations">{variation_options}</div>);
	}

	if (children) innerChildren.push(children)

	return (
		<span
			data-component="ProductAttribute"
			data-has-overrides={!!(fontSize || fontWeight)}
			data-type={attribute.type}
			data-reference={attribute.reference.toLowerCase()}
			data-position={attribute.position}
			data-value={data}
			className={classes}
			id={attribute.reference}
			key={attribute.reference}
			{...dataset}
		>
			{innerChildren}
		</span>
	);
}
